import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get leave types (vacations)
export const getLeaveTypes = createAsyncThunk(
  "LeaveTypes/getLeaveTypes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      // Use provided language or fallback to current language
      const currentLang = params.language || getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);

      const response = await axios.get(
        `${BASE_URL}/leave-types?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get leave types!";
      return rejectWithValue(message);
    }
  },
);

// Add leave type
export const addLeaveType = createAsyncThunk(
  "LeaveTypes/addLeaveType",
  async (leaveTypeData, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const currentLang = getCurrentLanguage();
      const response = await axios.post(
        `${BASE_URL}/leave-types`,
        leaveTypeData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add leave type!";
      return rejectWithValue(message);
    }
  },
);

// Update leave type
export const updateLeaveType = createAsyncThunk(
  "LeaveTypes/updateLeaveType",
  async ({ id, leaveTypeData }, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const currentLang = getCurrentLanguage();
      const response = await axios.put(
        `${BASE_URL}/leave-types/${id}`,
        leaveTypeData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update leave type!";
      return rejectWithValue(message);
    }
  },
);

// Delete leave type
export const deleteLeaveType = createAsyncThunk(
  "LeaveTypes/deleteLeaveType",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/leave-types/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete leave type!";
      return rejectWithValue(message);
    }
  },
);

// Get leave type details
export const getLeaveTypeDetails = createAsyncThunk(
  "LeaveTypes/getLeaveTypeDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/leave-types/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get leave type details!";
      return rejectWithValue(message);
    }
  },
);

// Toggle leave type status
export const toggleLeaveTypeStatus = createAsyncThunk(
  "LeaveTypes/toggleLeaveTypeStatus",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.patch(
        `${BASE_URL}/leave-types/${id}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to toggle leave type status!";
      return rejectWithValue(message);
    }
  },
);
